import { RslReport } from '@features/campaigns/subdomains/rsl';
import { FormSchema } from '@features/lci-report/components/form-schema';
import { Campaign, UseCalculatedFieldsReturn } from '@features/campaigns';
import { Presentation, ImageWithMetadata } from '@features/powerpoint';
import { titleSlide } from '@features/lci-report/slides';
import { createSharedPresentationConfig } from '@features/lci-report/utils/shared-presentation-config';
import { Lci3DimensionTableData } from '@features/lci-report/models/lci3-types';
import type {
  Lci3Report,
  Subdimension,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { overviewSlideFromProps } from './build-overview-slide';
import { executiveSummarySlideFromProps } from './build-executive-summary-slide';
import { salesAttributionSlides } from '../slides/sales-attribution-slides';
import { visitAttributionSlides } from '@features/lci-report/subdomains/visitation/slides/visit-attribution/build-visit-attribution-slides';
import { campaignAnalyticsSlides } from '../slides/campaign-analytics-slides';
import { appendixSlidesFromProps } from '../slides/appendix-slides';
import {
  getYoutubeProjectionFactor,
  transformSalesMetrics,
  transformValue,
  transformVisitMetrics,
} from '@features/reports/domain/youtube-measurement';
import { buildPresentationV2 } from './v2/build-presentation';

export type DesignVersion = 'v1' | 'v2';

export type BuildPresentationProps = {
  campaign: Pick<Campaign, 'id' | 'name' | 'startDate' | 'endDate'>;
  selectedReportDate: string;
  brandLogo: ImageWithMetadata | null;
  customFields: FormSchema;
  report: RslReport;
  salesSummary: Lci3Report['salesMetrics'] | null;
  visitMetrics: Lci3Report['visitMetrics'];
  subdimensions: Subdimension[];
  dimensionTableData: Lci3DimensionTableData;
  visitDimensionTableData: Lci3DimensionTableData;
  campaignAnalyticsReport: CampaignAnalyticsReport | null;
  calculatedFields: UseCalculatedFieldsReturn['data'];
  designVersion?: DesignVersion;
};

const transformReportForYoutube = (
  report: RslReport,
  factor: number
): RslReport => ({
  ...report,
  impressions: transformValue(report.impressions, factor) ?? report.impressions,
  uniqueUsers: transformValue(report.uniqueUsers, factor),
  matchedForAttribution: transformValue(report.matchedForAttribution, factor),
});

const transformDimensionTableDataForYoutube = (
  dimensionTableData: Lci3DimensionTableData,
  factor: number
): Lci3DimensionTableData =>
  Object.fromEntries(
    Object.entries(dimensionTableData).map(([key, subdimensions]) => [
      key,
      subdimensions.map(sub => ({
        ...sub,
        visitMetrics: transformVisitMetrics(sub.visitMetrics, factor),
        salesMetrics: sub.salesMetrics
          ? transformSalesMetrics(sub.salesMetrics, factor)
          : null,
      })),
    ])
  );

export const applyYoutubeTransformations = (
  props: BuildPresentationProps
): BuildPresentationProps => {
  const factor = getYoutubeProjectionFactor(
    props.customFields.youtubeImpressions,
    props.report.impressions
  );

  if (!factor) return props;

  return {
    ...props,
    report: transformReportForYoutube(props.report, factor),
    dimensionTableData: transformDimensionTableDataForYoutube(
      props.dimensionTableData,
      factor
    ),
    visitDimensionTableData: transformDimensionTableDataForYoutube(
      props.visitDimensionTableData,
      factor
    ),
  };
};

const buildPresentationV1 = (props: BuildPresentationProps): Presentation => {
  const transformedProps = applyYoutubeTransformations(props);

  const sharedConfig = createSharedPresentationConfig({
    campaign: transformedProps.campaign,
    brandLogo: transformedProps.brandLogo,
  });

  const slideMasterTitle = sharedConfig.slideMasters[0]?.title ?? '';

  return {
    ...sharedConfig,
    filename: `${transformedProps.campaign.name}_lci_report_${transformedProps.selectedReportDate}.pptx`,
    slides: [
      titleSlide({
        id: transformedProps.campaign.id,
        name: transformedProps.campaign.name,
      }),
      ...overviewSlideFromProps(transformedProps, slideMasterTitle),
      ...executiveSummarySlideFromProps(transformedProps, slideMasterTitle),
      ...(transformedProps.report.hasVisitMetrics
        ? visitAttributionSlides(
            {
              ...transformedProps,
              dimensionTableData: transformedProps.visitDimensionTableData,
            },
            slideMasterTitle
          )
        : []),
      ...salesAttributionSlides(transformedProps, slideMasterTitle),
      ...campaignAnalyticsSlides(transformedProps, slideMasterTitle),
      ...appendixSlidesFromProps(transformedProps),
    ],
  };
};

export const buildPresentation = (
  props: BuildPresentationProps
): Presentation =>
  props.designVersion === 'v2'
    ? buildPresentationV2(props)
    : buildPresentationV1(props);
